"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { AccountAreaType } from '@/types/menu';
import { ButtonStyleType } from "@/types";

interface HeaderSheetContextType {
  isSheetOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
  toggleSheet: () => void;
  accountArea: AccountAreaType | null;
  setAccountArea: (accountArea: AccountAreaType | null) => void;
  customAccountArea: CustomAccountAreaType | null;
  setCustomAccountArea: (customAccountArea: CustomAccountAreaType | null) => void;
}

interface CustomAccountAreaType {
  customAccountArea: boolean;
  withPartnerLogo: boolean;
  partnerLogo: string;
  customText: string;
  customButtonText: string;
  customButtonLink: string;
  customButtonStyle: ButtonStyleType;
}

const HeaderSheetContext = createContext<HeaderSheetContextType | undefined>(undefined);

export function HeaderSheetProvider({ children }: { children: React.ReactNode }) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [accountArea, setAccountArea] = useState<AccountAreaType | null>(null);
  const [customAccountArea, setCustomAccountArea] = useState<CustomAccountAreaType | null>(null);
  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);

  const openSheet = useCallback(() => setIsSheetOpen(true), []);
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);
  const toggleSheet = useCallback(() => setIsSheetOpen(prev => !prev), []);
  const setAccountAreaCallback = useCallback((accountArea: AccountAreaType | null) => setAccountArea(accountArea), []);
  const setCustomAccountAreaCallback = useCallback((customAccountArea: CustomAccountAreaType | null) => setCustomAccountArea(customAccountArea), []);

  // Close the menu when the route changes
  useEffect(() => {
    if (prevPathnameRef.current !== pathname && isSheetOpen) {
      setIsSheetOpen(false);
    }
    prevPathnameRef.current = pathname;
  }, [pathname, isSheetOpen]);

  return (
    <HeaderSheetContext.Provider value={{
      isSheetOpen,
      openSheet,
      closeSheet,
      toggleSheet,
      accountArea,
      setAccountArea: setAccountAreaCallback,
      customAccountArea,
      setCustomAccountArea: setCustomAccountAreaCallback,
    }}>
      {children}
    </HeaderSheetContext.Provider>
  );
}

export function useHeaderSheet() {
  const context = useContext(HeaderSheetContext);
  if (context === undefined) {
    throw new Error('useHeaderSheet must be used within a HeaderSheetProvider');
  }
  return context;
} 